// ============================================================
// TOKI PONA DATA
// Glyph and word data for toki pona text system
// ============================================================

// Spritesheet layout: 12 columns x 10 rows = 120 glyphs
export const GLYPH_COLS = 12;
export const GLYPH_ROWS = 10;
export const GLYPH_SIZE = 8; // 8x8 pixels per glyph
export const GLYPH_BUFFER = 4; // 4px buffer between glyphs in selection UI

// Special glyph indices (by word name)
export const SPECIAL_GLYPHS = {
    ENTER: 'pona',      // Confirm/send
    BACKSPACE: 'weka',  // Delete/back
    ESCAPE: 'ala',      // Cancel/close
} as const;

// Word list in spritesheet order (row-major: left-to-right, top-to-bottom)
// This matches the toki_pona.txt file order
const WORD_LIST: string[] = [
    'a', 'akesi', 'ala', 'alasa', 'ale', 'anpa', 'ante', 'anu', 'awen', 'e', 'en', 'esun',
    'ijo', 'ike', 'ilo', 'insa', 'jaki', 'jan', 'jelo', 'jo', 'kala', 'kalama', 'kama', 'kasi',
    'ken', 'kepeken', 'kili', 'kiwen', 'ko', 'kon', 'kule', 'kulupu', 'kute', 'la', 'lape', 'laso',
    'lawa', 'len', 'lete', 'li', 'lili', 'linja', 'lipu', 'loje', 'lon', 'luka', 'lukin', 'lupa',
    'ma', 'mama', 'mani', 'meli', 'mi', 'mije', 'moku', 'moli', 'monsi', 'mu', 'mun', 'musi',
    'mute', 'nanpa', 'nasa', 'nasin', 'nena', 'ni', 'nimi', 'noka', 'o', 'olin', 'ona', 'open',
    'pakala', 'pali', 'palisa', 'pan', 'pana', 'pi', 'pilin', 'pimeja', 'pini', 'pipi', 'poka', 'poki',
    'pona', 'pu', 'sama', 'seli', 'selo', 'seme', 'sewi', 'sijelo', 'sike', 'sin', 'sina', 'sinpin',
    'sitelen', 'sona', 'soweli', 'suli', 'suno', 'supa', 'suwi', 'tan', 'taso', 'tawa', 'telo', 'tenpo',
    'toki', 'tomo', 'tu', 'unpa', 'uta', 'utala', 'walo', 'wan', 'waso', 'wawa', 'weka', 'wile',
];

// English translations in spritesheet order (index-aligned with WORD_LIST)
// This matches the toki_pona_english_120.txt file order
const ENGLISH_LIST: string[] = [
    'ah', 'reptile', 'no', 'hunt', 'all', 'down', 'different', 'or', 'stay', 'marks object', 'and', 'shop',
    'thing', 'bad', 'tool', 'inside', 'gross', 'person', 'yellow', 'have', 'fish', 'sound', 'come', 'plant',
    'can', 'use', 'fruit', 'rock', 'paste', 'air', 'color', 'group', 'hear', 'context', 'sleep', 'blue',
    'head', 'cloth', 'cold', 'marks verb', 'small', 'string', 'paper', 'red', 'at', 'hand', 'see', 'hole',
    'land', 'parent', 'money', 'woman', 'I', 'man', 'eat', 'dead', 'back', 'moo', 'moon', 'fun',
    'many', 'number', 'weird', 'way', 'hill', 'this', 'word', 'foot', 'do', 'love', 'they', 'start',
    'broken', 'work', 'stick', 'bread', 'give', 'of', 'feel', 'black', 'done', 'bug', 'side', 'box',
    'good', 'the book', 'same', 'fire', 'skin', 'what', 'above', 'body', 'circle', 'new', 'you', 'front',
    'picture', 'know', 'animal', 'big', 'sun', 'table', 'sweet', 'from', 'but', 'to', 'water', 'time',
    'talk', 'house', 'two', 'sex', 'mouth', 'fight', 'white', 'one', 'bird', 'strong', 'away', 'want',
];

// Game object category per word (index-aligned with WORD_LIST)
// From game_object_categories_120.txt, ordered to match WORD_LIST
const CATEGORY_LIST: string[] = [
    'Emotes', 'Reptiles', 'Resistances', 'Gathering Nodes', 'Dimensions', 'Floor Tiles', 'Mutations', 'RNG Tables', 'Shields', 'Hitboxes', 'Joinery Recipes', 'Merchants',
    'Key Items', 'Curses', 'Tools', 'Organs', 'Diseases', 'NPCs', 'Lightning Spells', 'Small Containers', 'Fish', 'Instruments', 'World Events', 'Flora',
    'Abilities', 'Crafting', 'Fruits', 'Blocks', 'Sands', 'Gases', 'Colors', 'Factions', 'Soundscapes', 'Triggers', 'Dreams', 'Water Spells',
    'Helmets', 'Clothing', 'Ice', 'Actions', 'Seeds', 'Chains', 'Playing Cards', 'Fire Spells', 'Maps', 'Gloves', 'Draw Modes', 'Portal Runes',
    'Biomes', 'Spawners', 'Coins', 'Goddesses', 'Player Classes', 'Gods', 'Foods', 'Gravestones', 'Cloaks', 'Noises', 'Stars', 'Minigames',
    'Hordes', 'Numbers', 'Gag Items', 'Religions', 'Formations', 'Signs', 'Nametags', 'Shoes', 'Commands', 'Love Songs', 'Djinn', 'Keys',
    'Traps', 'Crafting Tables', 'Swords', 'Crops', 'Projectiles', 'Titles', 'Heart Containers', 'Black Magic', 'Fossils', 'Insects', 'Equipment Slots', 'Big Containers',
    'Buffs', 'Dictionary Pages', '???', 'Energy Sources', 'Dyes', 'Word Puzzles', 'Weathers', 'Body Shapes', 'Gears', 'Seasons', 'Nemeses', 'Paintings',
    'Glyphs', 'Skill Trees', 'Animals', 'Bosses', 'Light Sources', 'Furniture', 'Pets', 'Lore Cards', 'Debuffs', 'Vehicles', 'Liquids', 'Time',
    'Languages', 'Buildings', 'Pairs', 'Genes', 'Doors', 'Combat Styles', 'White Magic', 'Uniques', 'Birds', 'Power-ups', 'Decay Effects', 'Desires',
];

// Word to index mapping for quick lookup
const wordToIndex: Map<string, number> = new Map();
WORD_LIST.forEach((word, index) => {
    wordToIndex.set(word, index);
});

// Per-glyph colors (RGB) — index-aligned with WORD_LIST
// These are used to tint glyphs in the keyboard/selection UI
const GLYPH_COLORS: [number, number, number][] = [
    [246,195,67],[62,107,62],[17,17,17],[240,160,50],[110,68,255],[74,46,27],[107,79,207],[196,149,110],[176,144,96],[168,168,168],[100,180,160],[112,160,112],
    [176,168,144],[120,100,58],[112,136,144],[200,110,110],[107,90,46],[255,0,0],[248,210,58],[208,168,112],[124,158,184],[216,160,80],[80,176,80],[76,175,80],
    [90,138,90],[200,136,80],[122,170,85],[0,255,255],[212,200,176],[184,216,232],[176,104,176],[46,107,63],[201,135,110],[138,127,186],[48,42,120],[26,154,148],
    [224,198,144],[200,192,176],[168,210,232],[158,170,179],[240,200,208],[160,160,176],[240,232,208],[204,51,51],[208,160,96],[217,168,102],[74,144,196],[106,128,112],
    [139,109,63],[194,149,107],[208,168,80],[232,143,160],[232,193,112],[91,127,166],[255,140,66],[88,88,96],[176,120,120],[255,224,102],[192,207,224],[232,168,96],
    [212,184,90],[96,128,160],[128,64,192],[136,152,128],[196,144,112],[122,154,184],[56,80,138],[160,82,45],[201,168,124],[224,80,106],[160,160,160],[80,176,160],
    [216,64,64],[200,88,40],[160,128,96],[216,184,96],[80,168,112],[138,138,138],[222,122,138],[17,17,30],[74,74,90],[60,48,40],[201,149,107],[192,120,80],
    [80,184,88],[200,168,120],[203,213,192],[224,90,45],[212,161,144],[224,208,104],[48,96,160],[204,126,126],[160,184,208],[77,184,165],[108,180,217],[184,176,160],
    [176,128,160],[136,176,136],[144,104,64],[41,86,163],[245,200,66],[200,184,152],[248,160,200],[160,120,72],[192,184,168],[74,160,212],[61,142,191],[176,141,87],
    [80,160,208],[192,118,74],[184,160,128],[217,103,138],[212,70,107],[208,48,48],[240,236,228],[224,224,208],[135,181,212],[255,167,51],[176,184,192],[240,136,48],
];

// Toki pona particle/grammar words — these get the fast (1.5x) audio variant
const PARTICLE_WORDS: Set<string> = new Set([
    'a', 'ala', 'anu', 'e', 'en', 'la', 'li', 'ni', 'o', 'pi', 'taso',
]);

/**
 * Check if a word (by glyph index) is a particle
 */
export function isParticle(index: number): boolean {
    const word = getWord(index);
    return word !== null && PARTICLE_WORDS.has(word);
}

/**
 * Choose audio speed key for each word in a message.
 * - Particles always get '1.5' (fastest)
 * - If a single non-particle word is alone, it gets '0.5' (slowest/longest)
 * - Otherwise non-particles get '1.0' (normal)
 */
export type SpeedKey = '0.5' | '1.0' | '1.5';

export function getWordSpeeds(wordIndices: number[]): SpeedKey[] {
    const nonParticleCount = wordIndices.filter(i => !isParticle(i)).length;
    return wordIndices.map(index => {
        if (isParticle(index)) return '1.5';
        if (nonParticleCount === 1) return '0.5';
        return '1.0';
    });
}

// English translations (for tooltips/accessibility)
const TRANSLATIONS: Record<string, string> = {
    a: 'Emphasis', akesi: 'LIZARD', ala: 'NOT', alasa: 'HUNT', ale: 'ALL',
    anpa: 'BELOW', ante: 'DIFFERENT', anu: 'OR', awen: 'WAIT', e: 'Direct object',
    en: 'AND', esun: 'SHOP', ijo: 'THING', ike: 'BAD', ilo: 'TOOL',
    insa: 'INSIDE', jaki: 'DIRTY', jan: 'PERSON', jelo: 'YELLOW', jo: 'HAVE',
    kala: 'FISH', kalama: 'SOUND', kama: 'COME', kasi: 'PLANT', ken: 'CAN',
    kepeken: 'USE', kili: 'FRUIT', kiwen: 'BLOCK', ko: 'CLAY', kon: 'AIR',
    kule: 'COLOR', kulupu: 'TRIBE', kute: 'HEAR', la: 'Between context and main',
    lape: 'SLEEP', laso: 'BLUEGREEN', lawa: 'HEAD', len: 'CLOTHING', lete: 'COLD',
    li: 'Between subject and verb', lili: 'SMALL', linja: 'ROPE', lipu: 'PAPER',
    loje: 'RED', lon: 'BE', luka: 'HAND', lukin: 'SEE', lupa: 'DOOR',
    ma: 'WORLD', mama: 'PARENT', mani: 'MONEY', meli: 'WOMAN', mi: 'ME',
    mije: 'MAN', moku: 'EAT', moli: 'DIE', monsi: 'BACK', mu: 'MOO!',
    mun: 'MOON', musi: 'PLAY', mute: 'MANY', nanpa: 'NUMBER', nasa: 'CRAZY',
    nasin: 'WAY', nena: 'BUMP', ni: 'THIS', nimi: 'NAME', noka: 'FOOT',
    o: 'Vocative', olin: 'LOVE', ona: 'THEY', open: 'BEGIN', pakala: 'BREAK',
    pali: 'DO', palisa: 'STICK', pan: 'GRAIN', pana: 'GIVE', pi: 'OF',
    pilin: 'FEEL', pimeja: 'BLACK', pini: 'END', pipi: 'BUG', poka: 'SIDE',
    poki: 'CONTAINER', pona: 'GOOD', pu: 'Interact with the official toki pona book',
    sama: 'SAME', seli: 'HOT', selo: 'SURFACE', seme: 'Question', sewi: 'ABOVE',
    sijelo: 'BODY', sike: 'ROUND', sin: 'NEW', sina: 'YOU', sinpin: 'FRONT',
    sitelen: 'DRAW', sona: 'KNOW', soweli: 'ANIMAL', suli: 'BIG', suno: 'SUN',
    supa: 'TABLE', suwi: 'SWEET', tan: 'BECAUSE OF', taso: 'BUT', tawa: 'MOVE',
    telo: 'WATER', tenpo: 'TIME', toki: 'SAY', tomo: 'BUILDING', tu: 'TWO',
    unpa: 'UNITE', uta: 'MOUTH', utala: 'FIGHT', walo: 'WHITE', wan: 'ONE',
    waso: 'BIRD', wawa: 'STRONG', weka: 'AWAY', wile: 'NEED',
};

/**
 * Get the tint color for a glyph as a Phaser-compatible hex number (0xRRGGBB)
 */
export function getGlyphColor(index: number): number {
    if (index < 0 || index >= GLYPH_COLORS.length) return 0xffffff;
    const [r, g, b] = GLYPH_COLORS[index];
    return (r << 16) | (g << 8) | b;
}

/**
 * Get glyph index for a toki pona word
 */
export function getGlyphIndex(word: string): number {
    const index = wordToIndex.get(word.toLowerCase());
    return index !== undefined ? index : -1;
}

/**
 * Get word at a given glyph index
 */
export function getWord(index: number): string | null {
    return index >= 0 && index < WORD_LIST.length ? WORD_LIST[index] : null;
}

/**
 * Get English translation at a given glyph index
 */
export function getEnglish(index: number): string | null {
    return index >= 0 && index < ENGLISH_LIST.length ? ENGLISH_LIST[index] : null;
}

/**
 * Get game object category at a given glyph index
 */
export function getCategory(index: number): string | null {
    return index >= 0 && index < CATEGORY_LIST.length ? CATEGORY_LIST[index] : null;
}

/**
 * Get all words
 */
export function getAllWords(): readonly string[] {
    return WORD_LIST;
}

/**
 * Get English translation for a word
 */
export function getTranslation(word: string): string {
    return TRANSLATIONS[word.toLowerCase()] || word;
}

/**
 * Get glyph position in spritesheet (column, row)
 */
export function getGlyphPosition(index: number): { col: number; row: number } {
    return {
        col: index % GLYPH_COLS,
        row: Math.floor(index / GLYPH_COLS),
    };
}

/**
 * Get glyph pixel coordinates in spritesheet
 */
export function getGlyphPixelCoords(index: number): { x: number; y: number } {
    const pos = getGlyphPosition(index);
    return {
        x: pos.col * GLYPH_SIZE,
        y: pos.row * GLYPH_SIZE,
    };
}

/**
 * Parse a toki pona sentence string into word indices
 * Returns array of glyph indices, -1 for unknown words
 */
export function parseSentence(sentence: string): number[] {
    const words = sentence.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
    return words.map(word => getGlyphIndex(word));
}

/**
 * Get the total number of glyphs
 */
export function getGlyphCount(): number {
    return WORD_LIST.length;
}

/**
 * Calculate display duration for a message based on word count
 * Base time + additional time per word
 */
export function getDisplayDuration(wordCount: number): number {
    const BASE_MS = 2000; // 2 second minimum
    const PER_WORD_MS = 500; // 0.5 seconds per word
    return BASE_MS + wordCount * PER_WORD_MS;
}
